/*** This file contains helper functions that manipulate numbers ***/
/*
 * This method takes a float total and int buckets and evenly
 * distributes the total into the buckets with specified # precision digits.
 * Returns an array of float values.
 */

const evenlyDistribute = (total, buckets, precisionDigits) => {
  let p = Math.pow(10, precisionDigits), // precision
      m = +(total * p).toFixed(0), // integerized total - this would sometimes give a tiny fraction because JS so force it to have no decimal
      n = m % buckets, // remainder to distribute
      v = Math.floor(m / buckets) / p, // normal distribution cast to float
      w = Math.floor(m / buckets + 1) / p, // distribution with remainder cast to float
      out = new Array(buckets) // distributed values array
  for(let i = 0; i < buckets; ++i) {
    out[i] = i < n ? w : v; // distribute the correct number of normal and normal + remainder items
  }
  return out;
}

// precision version of parseFloat that is type safe - defaults to 0.00..{precision}
const tryParseFloat = (num, precision) => {
  let parsedFinal = +parseFloat('0').toFixed(precision);
  if(typeof num === 'string') {
    const parsedInitial = parseFloat(num.replace(/,/g, ''));
    if(isNaN(parsedInitial)) return parsedFinal;
    else {
      parsedFinal = +parsedInitial.toFixed(precision);
    }
  } else if(typeof num === 'number'){
    parsedFinal = +num.toFixed(precision);
  }
  return parsedFinal;
}

// returns a number in the localized format with n digit precision
const localizeNum = (num, precision=0, trailingZeros=false) => {
  const keepUserDigitsUpToPrecision = num => {
    let final = null;
    let numArr = num.toString().split('.');
    if(trailingZeros && numArr.length == 1) numArr.push('')

    if(!precision || numArr.length === 1) final = parseInt(numArr[0]).toLocaleString('en-US');
    else if(numArr.length === 2) {
      const isBetweenZeroAndNegOne = numArr[0] === '-0'; //need to reapply the negative in this case since -0 will parse to 0
      let rawSuffix = numArr[1];
      if (trailingZeros && rawSuffix.length < precision) rawSuffix = rawSuffix + Array(precision - rawSuffix.length + 1).join('0')

      let prefix = parseInt(numArr[0]).toLocaleString('en-US');
      if(rawSuffix.length <= 0) final = prefix;
      else if(rawSuffix.length > precision) {
        const precisionSuffix = parseFloat(`1.${rawSuffix}`).toFixed(precision).split('.')[1];
        final = isBetweenZeroAndNegOne ? [`-${prefix.replace('-', '')}`, precisionSuffix].join('.') : [prefix, precisionSuffix].join('.');
      }
      else final = isBetweenZeroAndNegOne ? [`-${prefix.replace('-', '')}`, rawSuffix].join('.') : [prefix, rawSuffix].join('.');
    }
    return final;
  }
  if(num !== null && num !== 'undefined') {
    if(typeof num === 'number') {
      return keepUserDigitsUpToPrecision(num);
    } else {
      // if a string is passed in, default to casting it to an integer
      if(typeof num === 'string') {
        let parsedInitial = parseFloat(num);
        if(isNaN(parsedInitial)) return null; // if the string could not be cast return null
        return keepUserDigitsUpToPrecision(parsedInitial);
      } else return null; // if any other type is passed in return null
    }
  }
}

const getNumForDisplay = (num) => {
  num = localizeNum(num, 4);
  if(num) {
    // check to see if there is a significant float right of the decimal, otherwise only show the integer
    let splitValue = num.split('.');
    if(splitValue.length === 2) {
      num = splitValue[1].split('').some(digit => digit.toString() !== '0') ? splitValue.join('.') : splitValue[0];
    } else {
      num = splitValue[0];
    }
  }
  return num;
}

function humanize(num, precision=0, trailingZeros=false) {
  const suffi = ['', 'K', 'M', 'B']
  const scale = (Math.log(num)/Math.log(10))/3|0
  num = num / Math.pow(10, 3*scale)
  num = localizeNum(num, precision, trailingZeros)
  return num + suffi[scale]
}

function isValidNumber(num) {
  return !isNaN(num - parseFloat(num))
}

export { evenlyDistribute, tryParseFloat, localizeNum, getNumForDisplay, humanize, isValidNumber };
